import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import HODHeader from '../components/Header';
import LiveSessionCard from './components/LiveSessionCard';
import UpcomingSessionCard from './components/UpcomingSessionCard';
import ClassStatusTable from './components/ClassStatusTable';
import FacultyDropdown from './components/FacultyDropdown';
import ClassSearchView from './components/ClassSearchView';
import FacultyTimetableModal from './components/FacultyTimetableModal';
import ClassTimetableModal from './components/ClassTimetableModal';
import { 
  RefreshCw, 
  AlertCircle, 
  Clock, 
  Users,
  Eye,
  X,
  Activity,
  Calendar,
  Bell,
  School,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const LiveMonitoring = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [, setSelectedFaculty] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [facultyTimetable, setFacultyTimetable] = useState(null);
  const [classTimetable, setClassTimetable] = useState(null);
  const [showFacultyTimetableModal, setShowFacultyTimetableModal] = useState(false);
  const [showClassTimetableModal, setShowClassTimetableModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [viewMode] = useState('grid');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  
  // Pagination boundary counters
  const [livePage, setLivePage] = useState(0);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const itemsPerPage = 6;

  const tenantId = localStorage.getItem('tenantId');

  // Fetch live monitoring data - ONLY on manual refresh
  const fetchLiveData = useCallback(async (showLoading = true) => {
    if (!tenantId) {
      setError('Your login session has expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      
      // ✅ Updated to HOD endpoint
      const response = await api.get(`/api/hod/live-monitoring?tenantId=${tenantId}`);
      
      if (response.data.success) {
        const newData = response.data.data;
        
        // Prevent stale state scopes inside callback chains
        setLiveData(prevData => {
          if (prevData) {
            const oldLiveIds = new Set(prevData.liveSessions?.map(s => s.classId + s.timeSlot?.start) || []);
            const newSessions = newData.liveSessions?.filter(s => !oldLiveIds.has(s.classId + s.timeSlot?.start)) || [];
            
            if (newSessions.length > 0) {
              const notif = {
                id: Date.now(),
                type: 'live_started',
                message: `${newSessions.length} new class session${newSessions.length > 1 ? 's have' : ' has'} started`,
                timestamp: new Date(),
                read: false
              };
              setNotifications(prevNotifs => [notif, ...prevNotifs].slice(0, 10));
            }
          }
          return newData;
        });

        setLastUpdated(new Date());
        setError(null);
        setLivePage(0);
        setUpcomingPage(0);
      } else {
        setError(response.data.message || 'Could not load current school activity');
      }
    } catch (err) {
      console.error('Error fetching live data:', err);
      setError(err.response?.data?.message || 'Could not connect to the server. Please check your internet connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId]);

  // Fetch faculties list - HOD endpoint
  const fetchFaculties = useCallback(async () => {
    if (!tenantId) return;
    try {
      // ✅ Updated to HOD endpoint
      const response = await api.get(`/api/hod/faculties?tenantId=${tenantId}`);
      if (response.data.success) {
        setFaculties(response.data.data.faculties);
      }
    } catch (err) {
      console.error('Error fetching faculties:', err);
    }
  }, [tenantId]);

  // Fetch faculty timetable - HOD endpoint
  const fetchFacultyTimetable = useCallback(async (facultyId) => {
    if (!facultyId || !tenantId) return;
    try {
      setLoading(true);
      // ✅ Updated to HOD endpoint
      const response = await api.get(`/api/hod/faculty/${facultyId}/timetable?tenantId=${tenantId}`);
      if (response.data.success) {
        setFacultyTimetable(response.data.data);
        setShowFacultyTimetableModal(true);
      } else {
        alert(response.data.message || 'No schedule layout found for this selected instructor.');
      }
    } catch (err) {
      console.error('Error fetching faculty timetable:', err);
      alert(err.response?.data?.message || 'Failed to load teacher schedule');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch class timetable - HOD endpoint
  const fetchClassTimetable = useCallback(async (classItem) => {
    if (!classItem || !tenantId) return;
    try {
      setLoading(true);
      // ✅ Updated to HOD endpoint
      const response = await api.get(`/api/hod/class/${classItem.id}/timetable?tenantId=${tenantId}`);
      if (response.data.success) {
        setClassTimetable(response.data.data);
        setShowClassTimetableModal(true);
      } else {
        alert(response.data.message || 'No active schedule configured for this room space.');
      }
    } catch (err) {
      console.error('Error fetching class timetable:', err);
      alert(err.response?.data?.message || 'Failed to load room schedule');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const handleFacultySelect = (faculty) => {
    setSelectedFaculty(faculty);
    if (faculty) {
      fetchFacultyTimetable(faculty.facultyId);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    if (classItem) {
      fetchClassTimetable(classItem);
    }
  };

  const handleViewMore = (session) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  // Pagination handlers
  const nextLivePage = () => {
    const totalPages = Math.ceil((liveData?.liveSessions?.length || 0) / itemsPerPage);
    if (livePage < totalPages - 1) setLivePage(livePage + 1);
  };

  const prevLivePage = () => {
    if (livePage > 0) setLivePage(livePage - 1);
  };

  const nextUpcomingPage = () => {
    const totalPages = Math.ceil((liveData?.upcomingSessions?.length || 0) / itemsPerPage);
    if (upcomingPage < totalPages - 1) setUpcomingPage(upcomingPage + 1);
  };

  const prevUpcomingPage = () => {
    if (upcomingPage > 0) setUpcomingPage(upcomingPage - 1);
  };

  const getPaginatedSessions = (sessions, page) => {
    if (!sessions) return [];
    const start = page * itemsPerPage;
    const end = start + itemsPerPage;
    return sessions.slice(start, end);
  };

  useEffect(() => {
    fetchLiveData(true);
    fetchFaculties();
  }, [fetchLiveData, fetchFaculties]);

  if (loading && !liveData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <HODHeader />
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
          <div className="relative mb-4">
            <div className="animate-spin rounded-full h-11 w-12 border-4 border-emerald-100 border-t-emerald-600"></div>
          </div>
          <p className="text-gray-800 font-bold text-sm tracking-tight text-center">Loading monitor feeds...</p>
          <p className="text-[11px] text-gray-400 text-center mt-0.5">Please wait while we build your dashboard analytics.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <HODHeader />
        <div className="flex items-center justify-center min-h-[75vh] px-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="w-12 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-sm font-bold text-gray-900 mb-1">Feeds Connection Failed</h2>
            <p className="text-gray-400 text-xs mb-4">{error}</p>
            <button
              onClick={() => fetchLiveData(true)}
              className="w-full px-4 py-2 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 text-xs transition-colors shadow-2xs"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ FIXED: Destructure with all fields from backend
  const { 
    liveSessions = [], 
    upcomingSessions = [], 
    classSummary = {}, 
    classStatus = [], 
    currentTime, 
    currentDay, 
    currentDate,
    departmentInfo = null 
  } = liveData || {};

  const totalLive = liveSessions.length;
  const totalUpcoming = upcomingSessions.length;
  // ✅ FIXED: Use classSummary.totalActiveClasses instead of classStatus.length
  const totalClasses = classSummary?.totalActiveClasses || classStatus.length || 0;
  const totalOverrides = classSummary?.classesWithOverrides || 0;
  const departmentName = departmentInfo?.name || 'Department';

  const paginatedLive = getPaginatedSessions(liveSessions, livePage);
  const paginatedUpcoming = getPaginatedSessions(upcomingSessions, upcomingPage);
  const totalLivePages = Math.ceil(totalLive / itemsPerPage);
  const totalUpcomingPages = Math.ceil(totalUpcoming / itemsPerPage);

  // ✅ FIXED: Check totalClasses correctly
  if (totalClasses === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <HODHeader />
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          <div className="bg-white rounded-2xl shadow-2xs border border-gray-200 p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <School className="w-7 h-7 text-gray-400" />
            </div>
            <h2 className="text-base font-bold text-gray-800">No Active Classes for Your Department</h2>
            <p className="text-gray-400 text-xs mt-1 max-w-xs mx-auto">
              {departmentName !== 'Department' 
                ? `No active classes found for ${departmentName} today.` 
                : 'No active classes found for your department today.'}
            </p>
            <button
              onClick={() => fetchLiveData(true)}
              className="mt-5 px-4 py-2 bg-emerald-600 text-white font-bold text-xs rounded-xl hover:bg-emerald-700 shadow-2xs"
            >
              Check System
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <HODHeader />
      
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
        
        {/* ===== HERO MONITOR ADMINISTRATIVE CONTROL STRIP ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-xs">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3.5">
              <div className="p-3 bg-emerald-50/70 rounded-xl hidden md:block text-emerald-600 flex-shrink-0">
                <Activity className="w-5 h-5" />
              </div>
              <div className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-base sm:text-lg font-black text-gray-900 tracking-tight uppercase">
                    {departmentName} - Live Class Monitor
                  </h1>
                  <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold border border-green-200 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    Live Now
                  </span>
                  {totalOverrides > 0 && (
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-200 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {totalOverrides} Modified
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-gray-500 font-medium">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    {currentDay}, {new Date(currentDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="text-gray-200 hidden sm:inline">•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-gray-400" />
                    {new Date(currentTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                  <span className="text-gray-200 hidden sm:inline">•</span>
                  <span className="text-gray-400 font-normal">
                    Refreshed: {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Actions Filters Strip Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full lg:w-auto">
              <div className="relative self-end sm:self-auto flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2.5 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 rounded-xl border border-gray-200 bg-white transition-colors relative"
                  aria-label="Alert history records menu drawer"
                >
                  <Bell className="w-4 h-4" />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-red-500 rounded-full" />
                  )}
                </button>
                
                {showNotifications && notifications.length > 0 && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-50 text-[11px] font-medium text-gray-600">
                    <div className="px-3 py-1 text-gray-400 uppercase font-bold border-b border-gray-100 pb-1.5 mb-1">System Events</div>
                    {notifications.map((n) => (
                      <div key={n.id} className="px-3 py-1.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 truncate">{n.message}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-full sm:w-auto">
                <FacultyDropdown
                  faculties={faculties}
                  onSelect={handleFacultySelect}
                  placeholder="Lookup Instructor Schedule"
                />
              </div>

              <button
                type="button"
                onClick={() => fetchLiveData(true)}
                disabled={refreshing}
                className="w-full sm:w-auto justify-center px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-xs min-h-[42px]"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Syncing...' : 'Refresh Monitor'}
              </button>
            </div>
          </div>

          {/* Metric Indicators Band Counters */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-gray-100 text-xs font-bold">
            <div className="p-3 bg-gray-50/60 rounded-xl border border-gray-100">
              <span className="text-green-700 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Sessions In Progress</span>
              <p className="text-xl font-black text-gray-900">{totalLive}</p>
            </div>
            
            <div className="p-3 bg-gray-50/60 rounded-xl border border-gray-100">
              <span className="text-blue-700 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Upcoming Sessions</span>
              <p className="text-xl font-black text-gray-900">{totalUpcoming}</p>
            </div>
            
            <div className="p-3 bg-gray-50/60 rounded-xl border border-gray-100">
              <span className="text-purple-700 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Monitored Classrooms</span>
              <p className="text-xl font-black text-gray-900">{totalClasses}</p>
            </div>
            
            <div className="p-3 bg-gray-50/60 rounded-xl border border-gray-100">
              <span className="text-amber-700 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Schedule Modifications</span>
              <p className="text-xl font-black text-gray-900">{totalOverrides}</p>
            </div>
          </div>
        </div>

        {/* ===== CORE SPLIT FEEDS PANEL ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          
          {/* Ongoing Card feeds list elements columns */}
          <div className="lg:col-span-2 space-y-5">
            
            {/* Live Card Sessions block layout items */}
            {totalLive > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-2xs">
                <div className="flex items-center justify-between p-3.5 border-b border-gray-100 bg-gray-50/40">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span>Sessions Running Now ({totalLive})</span>
                  </div>
                  
                  {totalLivePages > 1 && (
                    <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-gray-200 text-[11px] font-bold">
                      <button
                        type="button"
                        onClick={prevLivePage}
                        disabled={livePage === 0}
                        className="p-0.5 hover:text-emerald-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-gray-600">{livePage + 1} / {totalLivePages}</span>
                      <button
                        type="button"
                        onClick={nextLivePage}
                        disabled={livePage >= totalLivePages - 1}
                        className="p-0.5 hover:text-emerald-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className={`p-4 ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}`}>
                  {paginatedLive.map((session, idx) => (
                    <LiveSessionCard 
                      key={idx} 
                      session={session} 
                      index={idx}
                      onViewMore={handleViewMore}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Card Sessions block layout items */}
            {totalUpcoming > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-2xs">
                <div className="flex items-center justify-between p-3.5 border-b border-gray-100 bg-gray-50/40">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Upcoming Sessions Scheduled ({totalUpcoming})</span>
                  </div>
                  
                  {totalUpcomingPages > 1 && (
                    <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-gray-200 text-[11px] font-bold">
                      <button
                        type="button"
                        onClick={prevUpcomingPage}
                        disabled={upcomingPage === 0}
                        className="p-0.5 hover:text-emerald-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-gray-600">{upcomingPage + 1} / {totalUpcomingPages}</span>
                      <button
                        type="button"
                        onClick={nextUpcomingPage}
                        disabled={upcomingPage >= totalUpcomingPages - 1}
                        className="p-0.5 hover:text-emerald-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className={`p-4 ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}`}>
                  {paginatedUpcoming.map((session, idx) => (
                    <UpcomingSessionCard 
                      key={idx} 
                      session={session} 
                      index={idx}
                      onViewMore={handleViewMore}
                    />
                  ))}
                </div>
              </div>
            )}

            {totalLive === 0 && totalUpcoming === 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-2xs">
                <Clock className="w-7 h-7 text-gray-300 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-gray-700">No sessions configured for today</h3>
                <p className="text-xs text-gray-400 mt-0.5">All monitored lecture halls are currently empty or idle.</p>
              </div>
            )}
          </div>

          {/* Right Column Component View: Find Class Selector */}
          <div className="space-y-5">
            <ClassSearchView
              onClassSelect={handleClassSelect}
              selectedClass={selectedClass}
              tenantId={tenantId}
            />
          </div>
        </div>

        {/* ===== CLASS STATUS COMPREHENSIVE SUMMARY MATRIX GRAPH ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
          <div className="p-3.5 border-b border-gray-100 bg-gray-50/40 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <h2 className="text-xs font-bold text-gray-800 uppercase tracking-wider">All Classroom Status Matrix</h2>
          </div>
          <div className="p-2 sm:p-4 max-h-[480px] overflow-y-auto">
            <ClassStatusTable classStatus={classStatus} />
          </div>
        </div>

        {/* ===== DETAILED VALUE INSIGHT CONTEXT MODAL OVERLAY PANEL ===== */}
        {showSessionModal && selectedSession && (
          <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-xs transition-opacity animate-fade-in">
            <div className="absolute inset-0" onClick={() => setShowSessionModal(false)} />
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col max-h-[85vh] z-10 animate-slide-up">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2 mb-1 sm:hidden flex-shrink-0" />
              
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/40 flex-shrink-0">
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
                    <Eye className="w-4 h-4 text-emerald-600" /> Session Insight
                  </h2>
                  <p className="text-xs text-gray-500 font-semibold mt-0.5 truncate">{selectedSession.className}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSessionModal(false)}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-400"
                  aria-label="Dismiss view overlay information screens"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs font-semibold">
                <div className="grid grid-cols-2 gap-4 text-gray-600">
                  <div>
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Subject Title</span>
                    <span className="font-bold text-gray-800 text-sm">{selectedSession.subject?.name || 'Unmapped Category'}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Instructor</span>
                    <span className="font-bold text-gray-800 text-sm">{selectedSession.faculty?.name || 'No assignment'}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Time Duration</span>
                    <span className="font-bold text-gray-700 font-mono text-sm">{selectedSession.timeSlot?.start} - {selectedSession.timeSlot?.end}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Room Allocation</span>
                    <span className="font-bold text-gray-800 text-sm">{selectedSession.room || 'Unassigned Area'}</span>
                  </div>
                  {selectedSession.batch && (
                    <div>
                      <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Student Batch Group</span>
                      <span className="font-bold text-gray-800 text-sm">{selectedSession.batch.name}</span>
                    </div>
                  )}
                  <div>
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Schedule Frame Type</span>
                    <span className={`inline-flex items-center gap-1 font-bold rounded mt-0.5 border ${
                      selectedSession.isOverride 
                        ? 'text-amber-700 bg-amber-50 border-amber-200 px-1.5' 
                        : 'text-green-700 bg-green-50 border-green-200 px-1.5'
                    }`}>
                      {selectedSession.isOverride ? 'Modified Session' : 'Standard Routine'}
                    </span>
                  </div>
                </div>
                
                {selectedSession.electiveGroup && (
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 space-y-1.5">
                    <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Elective Category Parameters</span>
                    <p className="font-bold text-gray-800">{selectedSession.electiveGroup.name}</p>
                    <p className="text-gray-400 text-[11px] font-normal">{selectedSession.electiveGroup.category}</p>
                    {selectedSession.electiveGroup.subjects && (
                      <div className="mt-2 space-y-1 pl-2 border-l-2 border-emerald-200 text-gray-500 text-[11px]">
                        {selectedSession.electiveGroup.subjects.map((s, i) => (
                          <div key={i}>
                            • {s.subjectName} ({s.subGroups?.length || 0} tracks)
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-gray-50 border-t border-gray-100 flex justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowSessionModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl text-xs"
                >
                  Close Insight
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== AUXILIARY SCHEDULE TIMETABLE MODAL DIALOG POPUPS ===== */}
        {showFacultyTimetableModal && facultyTimetable && (
          <FacultyTimetableModal
            timetable={facultyTimetable}
            onClose={() => {
              setShowFacultyTimetableModal(false);
              setFacultyTimetable(null);
            }}
          />
        )}

        {showClassTimetableModal && classTimetable && (
          <ClassTimetableModal
            timetable={classTimetable}
            classInfo={selectedClass}
            onClose={() => {
              setShowClassTimetableModal(false);
              setClassTimetable(null);
              setSelectedClass(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LiveMonitoring;